import { useEffect, useState, useMemo } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { lawyersScoringI18n } from './i18n';
import DatePickerEs from '../../components/DatePickerEs/DatePickerEs';
import { getLawyerScoringRanking } from '../../services/lawyersScoringService';
import { getBusinessAreasPaged } from '../../services/Businessareaservices';
import { sortByLocale, sortByNumber } from '../../utils/sortBy';
import { getCurrentUser } from '../../services/authService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './lawyersScoring.css';
import { excelExportLogService } from 'services/excelLogFromFront';
import usePermissionsReports from 'permissions/modules/reports/usePermissionsReports';

const SORT_BACKEND_KEYS = {
  lawyerName: 'Abogado',
  totalTasks: 'Total',
  pendingTasks: 'Pendientes',
  approvedTasks: 'Resueltas',
  averageApprovedHours: 'PromedioHoras',
  occupationPercentage: 'Ocupacion',
};

const LawyersScoring = ({ DesignSystemComponents }) => {
  const { Card, Dropdown, Table, Input, Button } = DesignSystemComponents;

  const { t } = useScopedTranslation(lawyersScoringI18n);

  const [userId, setUserId] = useState(null);

  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState(null);

  const initialStart = new Date();
  initialStart.setDate(initialStart.getDate() - 30);
  const initialEnd = new Date();

  const INITIAL_RANGE = {
    startDate: initialStart,
    endDate: initialEnd,
  };

  const [dateRange, setDateRange] = useState(INITIAL_RANGE);
  const [initialRange] = useState(INITIAL_RANGE);

  const [performance, setPerformance] = useState([]);
  const [nextData, setNextData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [safeLoading, setSafeLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const { canDownloadScoringByLawyersExcel } = usePermissionsReports();

  const [sortApi, setSortApi] = useState({
    sortKey: null,
    sortDesc: null,
  });

  const toISO = (date) =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();

  const formatDateHuman = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(
      d.getMonth() + 1
    ).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const mapAreasToDropdown = (areas) =>
    areas.map((a) => ({
      label: a.name,
      value: a.id,
    }));

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchAreas = async () => {
      try {
        const { businessAreas } = await getBusinessAreasPaged(
          1,
          50,
          null,
          true
        );
        if (!isMounted) return;

        const mapped = mapAreasToDropdown(businessAreas);
        setAreas(mapped);
        if (mapped.length > 0) setArea(mapped[0]);
      } catch (err) {
        console.error('Error loading areas:', err);
      }
    };

    fetchAreas();
    return () => (isMounted = false);
  }, []);

  useEffect(() => {
    if (!area || !dateRange) return;

    let isMounted = true;

    const fetchRanking = async () => {
      try {
        setLoading(true);
        setSafeLoading(true);

        const result = await getLawyerScoringRanking(
          area.value,
          toISO(dateRange.startDate),
          toISO(dateRange.endDate)
        );

        if (!isMounted) return;
        setNextData(result);
      } catch (err) {
        console.error('Error loading ranking:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchRanking();
    return () => (isMounted = false);
  }, [area, dateRange]);

  useEffect(() => {
    if (!loading && nextData !== null) {
      setPerformance(nextData);
      setNextData(null);
    }
  }, [loading, nextData]);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => setSafeLoading(false), 50);
    }
  }, [loading]);

  useEffect(() => setPage(0), [area, dateRange, search]);

  const normalize = (str) =>
    str
      ?.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const searchWords = normalize(search).split(' ').filter(Boolean);

  const filteredPerformance = useMemo(() => {
    return performance.filter((p) => {
      const name = normalize(p.lawyerName);
      return searchWords.every((w) => name.includes(w));
    });
  }, [performance, search]);

  const sortFns = {
    lawyerName: sortByLocale('lawyerName'),
    totalTasks: sortByNumber('totalTasks'),
    pendingTasks: sortByNumber('pendingTasks'),
    approvedTasks: sortByNumber('approvedTasks'),
    averageApprovedHours: sortByNumber('averageApprovedHours'),
    occupationPercentage: sortByNumber('occupationPercentage'),
  };

  const buildHumanFilters = () => {
    const filters = {};

    if (search?.trim()) filters[t('ls_filterSearchBy')] = search.trim();
    if (area) filters[t('ls_filterArea')] = area.label;

    if (dateRange?.startDate && dateRange?.endDate) {
      filters[t('ls_filterPeriod')] = `${formatDateHuman(
        dateRange.startDate
      )} - ${formatDateHuman(dateRange.endDate)}`;
    }

    return filters;
  };

  const averages = useMemo(() => {
    if (!filteredPerformance.length) {
      return { avgHours: 0, avgOccupation: 0 };
    }

    const totalHours = filteredPerformance.reduce(
      (acc, p) => acc + p.averageApprovedHours,
      0
    );
    const totalOccupation = filteredPerformance.reduce(
      (acc, p) => acc + p.occupationPercentage,
      0
    );

    return {
      avgHours: totalHours / filteredPerformance.length,
      avgOccupation: totalOccupation / filteredPerformance.length,
    };
  }, [filteredPerformance]);

  const sortedPerformance = useMemo(() => {
    if (!sortApi.sortKey) return filteredPerformance;

    const frontendKey = Object.entries(SORT_BACKEND_KEYS).find(
      ([, backend]) => backend === sortApi.sortKey
    )?.[0];

    if (!frontendKey) return filteredPerformance;

    const sorted = [...filteredPerformance].sort((a, b) => {
      const aVal = a[frontendKey];
      const bVal = b[frontendKey];

      if (typeof aVal === 'string') {
        return aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
      }

      return aVal - bVal;
    });

    return sortApi.sortDesc ? sorted.reverse() : sorted;
  }, [filteredPerformance, sortApi]);

  const downloadExcel = async () => {
    try {
      await excelExportLogService.log({
        userId,
        source: t('ls_excelSource'),
        sortKey: sortApi.sortKey,
        sortDesc: sortApi.sortDesc,
        filters: buildHumanFilters(),
      });
    } catch (error) {
      console.warn('Export log failed', error);
    }

    try {
      if (!sortedPerformance.length) return;

      const formatted = sortedPerformance.map((item) => ({
        [t('ls_excelCol_lawyer')]: item.lawyerName,
        [t('ls_excelCol_total')]: item.totalTasks,
        [t('ls_excelCol_pending')]: item.pendingTasks,
        [t('ls_excelCol_resolved')]: item.approvedTasks,
        [t('ls_excelCol_avgHours')]: Number(
          item.averageApprovedHours.toFixed(0)
        ),
        [t('ls_excelCol_occupation')]: Number(
          item.occupationPercentage.toFixed(0)
        ),
      }));

      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, t('ls_excelSheet'));

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });

      saveAs(blob, t('ls_excelFile'));
    } catch (error) {
      console.error('Error generating Excel:', error);
    }
  };

  return (
    <div>
      <div className='mb-4'>
        <div className='row'>
          <div className='col-7'>
            <h2 className='fw-semibold'>{t('ls_pageTitle')}</h2>
            <h4 className='fw-normal'>{t('ls_pageSubtitle')}</h4>
          </div>
          <div className='col-3'>
            <DatePickerEs
              initialRange={initialRange}
              onChange={(range) => setDateRange(range)}
            />
          </div>
          <div className='col-2'>
            <Dropdown
              value={area}
              onChange={(selected) => setArea(selected)}
              options={areas}
            />
          </div>
        </div>
      </div>

      <Card specialPadding='px-4 pt-4 pb-2'>
        <div className='row'>
          <div className='col-6 d-flex align-items-center'>
            <h3 className='fw-semibold mb-3'>{t('ls_tableTitle')}</h3>
          </div>
          <div className='col-4 d-flex align-items-center'>
            <Input
              icon='search'
              placeholder={t('ls_searchPlaceholder')}
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='w-100 me-3'
              disabled={!performance.length || loading}
            />
          </div>
          <div className='col-2 d-flex align-items-center justify-content-end'>
            <Button
              iconStart='download'
              label={t('ls_downloadExcel')}
              variant='naked'
              size='sm'
              className='mb-4'
              onClick={downloadExcel}
              disabled={
                !filteredPerformance.length || !canDownloadScoringByLawyersExcel
              }
            />
          </div>
        </div>
        <div className='table-container task-scoring'>
          <Table
            columns={[
              { accessor: 'lawyerName', label: t('ls_col_lawyer'), sort: true },
              { accessor: 'totalTasks', label: t('ls_col_total'), sort: true },
              { accessor: 'pendingTasks', label: t('ls_col_pending'), sort: true },
              { accessor: 'approvedTasks', label: t('ls_col_resolved'), sort: true },
              {
                accessor: 'averageApprovedHours',
                label: t('ls_col_avgHours'),
                sort: true,
                render: (info) => (
                  <p
                    className={`caption ${
                      info.averageApprovedHours < averages.avgHours
                        ? 'fw-bold color-error-500'
                        : 'fw-normal'
                    }`}
                  >
                    {info.averageApprovedHours.toFixed(0)}
                  </p>
                ),
              },
              {
                accessor: 'occupationPercentage',
                label: t('ls_col_occupation'),
                sort: true,
                render: (info) => (
                  <p
                    className={`caption ${
                      info.occupationPercentage < averages.avgOccupation
                        ? 'fw-bold color-error-500'
                        : 'fw-normal'
                    }`}
                  >
                    {info.occupationPercentage.toFixed(0)}
                  </p>
                ),
              },
            ]}
            data={filteredPerformance}
            page={page}
            onPageChange={setPage}
            initialPageSize={10}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '15', value: 15 },
            ]}
            loading={safeLoading}
            sortFns={sortFns}
            onClientSortChange={({ sortKey, direction }) => {
              if (!sortKey || !direction) {
                setSortApi({ sortKey: null, sortDesc: null });
                return;
              }
              const backendKey = SORT_BACKEND_KEYS[sortKey] ?? sortKey;
              setSortApi({
                sortKey: backendKey,
                sortDesc: direction === 'desc' ? true : false,
              });
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default LawyersScoring;
